import React, { useCallback, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  MapPin
} from '@phosphor-icons/react';
import TaskDistributionJourneyStep from './TaskDistributionJourneyStep.jsx';
import TaskDistributionStepNavigation from './TaskDistributionStepNavigation.jsx';
import TaskDistributionTasksStep from './TaskDistributionTasksStep.jsx';
import TaskDistributionTeamsStep from './TaskDistributionTeamsStep.jsx';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const TaskDistributionStepper = ({
  date,
  dateOptions,
  config,
  selectedPriorityPreset,
  teamItems,
  selectedTeamIds,
  taskTypeItems,
  selectedTaskTypeIds,
  taskItems,
  selectedTeamTaskItems,
  remainingTaskItems,
  hasSelectedTeamTasks,
  isLoadingTeamAvailability,
  selectedTaskIds,
  fixedTaskIds,
  isDateViewLoading,
  isCalculating,
  onDateChange,
  onConfigChange,
  onTeamsChange,
  onTaskTypesChange,
  onTasksChange,
  onToggleFixedTask,
  onRequestRemovePreassignment,
  onCalculate
}) => {
  const [activeStep, setActiveStep] = useState(1);
  const [teamSearch, setTeamSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [taskListMode, setTaskListMode] = useState('teams');

  // Recorrido guiado: los pasos con `pane` muestran ese paso del panel; al cerrar vuelve al que estaba.
  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;
  const stepBeforeTourRef = useRef(null);
  useGuidedTourStep(useCallback((tourStep) => {
    if (tourStep && stepBeforeTourRef.current === null) stepBeforeTourRef.current = activeStepRef.current;
    if (tourStep?.pane) setActiveStep(tourStep.pane);
    if (!tourStep && stepBeforeTourRef.current !== null) {
      setActiveStep(stepBeforeTourRef.current);
      stepBeforeTourRef.current = null;
    }
  }, []));

  const selectedDateOption = dateOptions.find((option) => option.value === date);
  const dateSummary = selectedDateOption
    ? `${selectedDateOption.dayLabel} ${String(selectedDateOption.weekdayLabel).slice(0, 3)}`
    : date;
  const dateLabel = [dateSummary, config.shiftStartTime].filter(Boolean).join(' · ') || 'Sin configurar';
  const isStepOneComplete = Boolean(date && config.shiftStartTime);
  const isStepTwoComplete = selectedTeamIds.length > 0 && !isLoadingTeamAvailability;
  const isStepThreeComplete = selectedTaskIds.length > 0 && !isDateViewLoading;
  const completedSteps = {
    1: isStepOneComplete,
    2: isStepTwoComplete,
    3: isStepThreeComplete
  };
  const maxAccessibleStep = !isStepOneComplete ? 1 : !isStepTwoComplete ? 2 : 3;
  const canContinue = activeStep === 1 ? isStepOneComplete : isStepTwoComplete;
  const canCalculate = isStepOneComplete
    && isStepTwoComplete
    && isStepThreeComplete
    && !isCalculating;

  const handlePrimaryAction = () => {
    if (activeStep < 3) {
      if (canContinue) {
        setActiveStep((current) => Math.min(3, current + 1));
      }
      return;
    }

    if (canCalculate) {
      onCalculate();
    }
  };

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-line bg-surface/90 shadow-[0_20px_60px_rgba(15,23,42,0.35)]">
      <header className="border-b border-line px-4 py-5">
        <div className="mb-4">
          <div className="text-sm font-bold uppercase tracking-[0.14em] text-content">Preparar distribución</div>
          <div className="mt-1 text-xs text-content-muted">
            Completa los tres pasos para configurar la distribución de tareas.
          </div>
        </div>
        <div data-tour="dist-stepnav">
        <TaskDistributionStepNavigation
          activeStep={activeStep}
          dateLabel={dateLabel}
          selectedTeamCount={selectedTeamIds.length}
          selectedTaskCount={selectedTaskIds.length}
          completedSteps={completedSteps}
          maxAccessibleStep={maxAccessibleStep}
          onStepChange={setActiveStep}
        />
        </div>
      </header>

      <div className="min-h-[520px] px-4 py-5">
        {activeStep === 1 && (
          <TaskDistributionJourneyStep
            date={date}
            dateOptions={dateOptions}
            config={config}
            selectedPriorityPreset={selectedPriorityPreset}
            onDateChange={onDateChange}
            onConfigChange={onConfigChange}
          />
        )}

        {activeStep === 2 && (
          <TaskDistributionTeamsStep
            teamItems={teamItems}
            selectedTeamIds={selectedTeamIds}
            isLoadingTeamAvailability={isLoadingTeamAvailability}
            searchValue={teamSearch}
            onSearchChange={setTeamSearch}
            onTeamsChange={onTeamsChange}
          />
        )}

        {activeStep === 3 && (
          <TaskDistributionTasksStep
            date={date}
            taskTypeItems={taskTypeItems}
            selectedTaskTypeIds={selectedTaskTypeIds}
            taskItems={taskItems}
            selectedTeamTaskItems={selectedTeamTaskItems}
            remainingTaskItems={remainingTaskItems}
            hasSelectedTeamTasks={hasSelectedTeamTasks}
            isDateViewLoading={isDateViewLoading}
            selectedTaskIds={selectedTaskIds}
            fixedTaskIds={fixedTaskIds}
            selectedTeamIds={selectedTeamIds}
            teamItems={teamItems}
            searchValue={taskSearch}
            listMode={taskListMode}
            onSearchChange={setTaskSearch}
            onListModeChange={setTaskListMode}
            onTaskTypesChange={onTaskTypesChange}
            onTasksChange={onTasksChange}
            onToggleFixedTask={onToggleFixedTask}
            onRequestRemovePreassignment={onRequestRemovePreassignment}
          />
        )}
      </div>

      <footer data-tour="dist-calc" className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
        <button
          type="button"
          onClick={() => setActiveStep((current) => Math.max(1, current - 1))}
          disabled={activeStep === 1}
          className="inline-flex min-w-[60px] items-center gap-1 text-xs font-semibold text-content-muted transition hover:text-content disabled:invisible"
        >
          <ArrowLeft size={14} />
          Atrás
        </button>

        <div className="min-w-0 flex-1 text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-2 text-[11px] font-semibold text-content">
            <span className="whitespace-nowrap">{selectedTeamIds.length} equipos</span>
            <span className="whitespace-nowrap">{selectedTaskIds.length} tareas</span>
          </div>
        </div>

        <button
          type="button"
          disabled={activeStep < 3 ? !canContinue : !canCalculate}
          onClick={handlePrimaryAction}
          className="inline-flex min-w-[112px] items-center justify-center gap-1.5 rounded-2xl bg-amber-hover px-4 py-3 text-xs font-bold text-on-amber transition hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {activeStep < 3 ? (
            <>
              Continuar
              <ArrowRight size={14} weight="bold" />
            </>
          ) : isCalculating ? (
            <>
              <ArrowsClockwise size={15} className="animate-spin" />
              Calculando
            </>
          ) : (
            <>
              <MapPin size={15} weight="fill" />
              Calcular rutas
            </>
          )}
        </button>
      </footer>
    </section>
  );
};

export default TaskDistributionStepper;
